import type { ReactNode } from 'react'
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react'

import { ToastHost } from '../components/feedback/ToastHost'

export type ToastVariant = 'info' | 'success' | 'error'

export interface ToastAction {
  label: string
  onPress: () => void
}

export interface ShowToastParams {
  title: string
  message: string
  variant?: ToastVariant
  durationMs?: number
  autoDismiss?: boolean
  action?: ToastAction
}

interface ToastItem {
  id: string
  title: string
  message: string
  variant: ToastVariant
  durationMs: number
  autoDismiss: boolean
  action?: ToastAction
}

interface ToastState {
  queue: ToastItem[]
}

type ToastActionType =
  | { type: 'enqueue'; toast: ToastItem }
  | { type: 'dismiss'; id: string }
  | { type: 'clear' }

const DEFAULT_TOAST_DURATION_MS = 5000

const ToastContext = createContext<{
  showToast: (params: ShowToastParams) => string
  dismissToast: (id: string) => void
  clearToasts: () => void
} | null>(null)

function createToastId(): string {
  if (
    typeof globalThis.crypto !== 'undefined' &&
    typeof globalThis.crypto.randomUUID === 'function'
  ) {
    return globalThis.crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function toastReducer(state: ToastState, action: ToastActionType): ToastState {
  switch (action.type) {
    case 'enqueue':
      return { queue: [...state.queue, action.toast] }
    case 'dismiss':
      return { queue: state.queue.filter((toast) => toast.id !== action.id) }
    case 'clear':
      return { queue: [] }
    default:
      return state
  }
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(toastReducer, { queue: [] })

  const dismissToast = useCallback((id: string) => {
    dispatch({ type: 'dismiss', id })
  }, [])

  const clearToasts = useCallback(() => {
    dispatch({ type: 'clear' })
  }, [])

  const showToast = useCallback((params: ShowToastParams) => {
    const id = createToastId()
    dispatch({
      type: 'enqueue',
      toast: {
        id,
        title: params.title,
        message: params.message,
        variant: params.variant ?? 'info',
        durationMs: params.durationMs ?? DEFAULT_TOAST_DURATION_MS,
        autoDismiss: params.autoDismiss ?? true,
        action: params.action,
      },
    })
    return id
  }, [])

  const currentToast = state.queue[0] ?? null
  const value = useMemo(
    () => ({ clearToasts, dismissToast, showToast }),
    [clearToasts, dismissToast, showToast],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastHost toast={currentToast} onDismiss={dismissToast} />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (context == null) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
